/**
 * Hilason AI Search Widget — Hybrid AI + Shopify Search
 *
 * Combines AI semantic + SKU search with Shopify's native predictive search.
 * AI results appear on top, Shopify keyword matches below — all in one dropdown.
 *
 * Add to theme.liquid before </body>:
 *   <script src="{{ 'hilason-ai-search.js' | asset_url }}" defer></script>
 * To disable: wrap that line in {% comment %}...{% endcomment %}
 * Emergency bypass: add ?ai=off to any URL
 */

const AI_SEARCH_URL =
  "https://hvlwtpedsioogfguleon.supabase.co/functions/v1/product-search";
const AI_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh2bHd0cGVkc2lvb2dmZ3VsZW9uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMjIwMzAsImV4cCI6MjA5MjU5ODAzMH0.f6uSpeFCyg5M5KdukiqO8xa-tyAO5wjzFGpYA9ZE4K4";

// Multi-store support: set window.AI_STORE_ID in your theme.liquid BEFORE this
// script loads to filter results to only this store's products.
// Example (theme.liquid): <script>window.AI_STORE_ID = "your-integration-uuid";</script>
const AI_STORE_ID = (typeof window !== "undefined" && window.AI_STORE_ID) || null;

const SEARCH_INPUT_SELECTOR = 'input[type="search"], input[name="q"]';
const MIN_CHARS = 3;
const DEBOUNCE_MS = 280;
const AI_TOP_LIMIT = 18;    // top AI results to show (fills 6 rows of 3 columns)
const SHOPIFY_LIMIT = 0;    // AI results are sufficient; Shopify section removed

const CSS = `
/* Hide the theme's search dropdown container entirely */
.quickSearchResultsWrap {
  display: none !important;
}
.pos_top {
  display: none !important;
}
#hilason-ai-results {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  left: auto;
  width: 660px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  z-index: 999999;
  max-height: 80vh;
  overflow-y: auto;
  font-family: inherit;
}
#hilason-ai-results .ai-section-header {
  padding: 9px 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #555;
  border-bottom: 1px solid #eee;
  background: #fafafa;
  display: flex;
  align-items: center;
  gap: 8px;
}
#hilason-ai-results .ai-see-all-top {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  color: #1a2744;
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
#hilason-ai-results .ai-see-all-top:hover { text-decoration: underline; }
#hilason-ai-results .ai-header-badge {
  background: #c0392b;
  color: #fff;
  font-size: 9px;
  padding: 2px 7px;
  border-radius: 2px;
  letter-spacing: 0.05em;
}
#hilason-ai-results .ai-header-badge.shopify {
  background: #5c6c7a;
}
#hilason-ai-results .ai-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}
#hilason-ai-results .ai-item {
  display: flex;
  flex-direction: column;
  padding: 12px;
  text-decoration: none;
  color: inherit;
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.12s;
  cursor: pointer;
  position: relative;
}
#hilason-ai-results .ai-item:hover,
#hilason-ai-results .ai-item:focus {
  background: #f7f8fa;
  outline: none;
}
#hilason-ai-results .ai-item:nth-child(3n) { border-right: none; }
#hilason-ai-results .ai-thumb-wrap {
  width: 100%;
  padding-bottom: 100%;
  position: relative;
  background: #f5f5f5;
  margin-bottom: 8px;
  border-radius: 2px;
  overflow: hidden;
}
#hilason-ai-results .ai-thumb {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}
#hilason-ai-results .ai-thumb-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
#hilason-ai-results .ai-brand {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 2px;
}
#hilason-ai-results .ai-title {
  font-size: 13px;
  font-weight: 500;
  color: #222;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
#hilason-ai-results .ai-price {
  font-size: 13px;
  font-weight: 700;
  color: #222;
  margin-top: auto;
}
#hilason-ai-results .ai-price-na {
  font-size: 12px;
  color: #aaa;
  margin-top: auto;
}
#hilason-ai-results .ai-score-exact {
  display: inline-block;
  font-size: 9px;
  background: #1a2744;
  color: #fff;
  padding: 1px 6px;
  border-radius: 2px;
  margin-bottom: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  align-self: flex-start;
}
#hilason-ai-results .ai-empty {
  padding: 28px 16px;
  text-align: center;
  color: #888;
  font-size: 14px;
}
#hilason-ai-results .ai-loading {
  padding: 20px;
  text-align: center;
  color: #aaa;
  font-size: 13px;
}
#hilason-ai-results .ai-footer {
  padding: 11px 14px;
  font-size: 12px;
  color: #555;
  text-align: center;
  border-top: 1px solid #eee;
  background: #fafafa;
}
#hilason-ai-results .ai-footer a {
  color: #1a2744;
  font-weight: 700;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}
#hilason-ai-results .ai-footer a:hover { text-decoration: underline; }
`;

class HilasonAISearch {
  constructor() {
    this._injectStyles();
    this._inputs = [];
    this._dropdown = null;
    this._activeInput = null;
    this._debounceTimer = null;
    this._lastQuery = "";
    this._observer = null;
    this._killInterval = null;
    this._init();
    this._killThemeSearchForever();
  }

  _injectStyles() {
    if (document.getElementById("hilason-ai-search-styles")) return;
    const style = document.createElement("style");
    style.id = "hilason-ai-search-styles";
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // Permanently suppress the theme's search dropdown (.quickSearchResultsWrap)
  // The theme's jQuery plugin calls .show() on it — we override that inline style immediately.
  _killThemeSearchForever() {
    const SELECTORS = ".quickSearchResultsWrap, .pos_top";

    const applyHide = (el) => {
      el.style.setProperty("display", "none", "important");
      if (!el._hilasonWatched) {
        el._hilasonWatched = true;
        // Re-suppress whenever the theme's jQuery .show() changes the style attribute
        new MutationObserver(() => {
          el.style.setProperty("display", "none", "important");
        }).observe(el, { attributes: true, attributeFilter: ["style"] });
      }
    };

    const scan = () => document.querySelectorAll(SELECTORS).forEach(applyHide);
    scan();

    this._observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(SELECTORS)) applyHide(node);
          node.querySelectorAll?.(SELECTORS).forEach(applyHide);
        }
      }
    });
    this._observer.observe(document.documentElement, { childList: true, subtree: true });
    this._killInterval = setInterval(scan, 200);
  }

  _init() {
    const attach = () => {
      document.querySelectorAll(SEARCH_INPUT_SELECTOR)
        .forEach((input) => this._attachInput(input));
    };
    attach();
    setTimeout(attach, 800);
    setTimeout(attach, 2000);
  }

  _attachInput(input) {
    if (input._hilasonAttached) return;
    input._hilasonAttached = true;
    this._inputs.push(input);

    const parent = input.parentElement;
    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }

    // Capture phase fires before jQuery bubble-phase handlers.
    // Block keyup + focusin — the actual triggers used by the theme's jQuery search plugin.
    input.addEventListener("keyup",   (e) => e.stopImmediatePropagation(), { capture: true });
    input.addEventListener("focusin", (e) => e.stopImmediatePropagation(), { capture: true });
    // input/focus: block theme then run ours
    input.addEventListener("input", (e) => { e.stopImmediatePropagation(); this._onInput(input); }, { capture: true });
    input.addEventListener("focus", (e) => {
      e.stopImmediatePropagation();
      if (input.value.length >= MIN_CHARS) this._onInput(input);
    }, { capture: true });
    input.addEventListener("keydown", (e) => this._onKeydown(e));

    // Intercept form submit (Enter key) and redirect to AI search page
    const form = input.closest("form");
    if (form && !form._hilasonSubmitAttached) {
      form._hilasonSubmitAttached = true;
      form.addEventListener("submit", (e) => {
        const q = input.value.trim();
        if (q.length >= MIN_CHARS) {
          e.preventDefault();
          window.location.href = `/pages/ai-search?q=${encodeURIComponent(q)}`;
        }
      });
    }

    document.addEventListener("click", (e) => {
      if (!input.contains(e.target) && !this._dropdown?.contains(e.target)) {
        this._hide();
      }
    });
  }

  _onInput(input) {
    clearTimeout(this._debounceTimer);
    const q = input.value.trim();
    if (q.length < MIN_CHARS) { this._hide(); return; }
    this._activeInput = input;
    this._debounceTimer = setTimeout(() => this._search(q), DEBOUNCE_MS);
  }

  _onKeydown(e) {
    if (!this._dropdown) return;
    const items = [...this._dropdown.querySelectorAll(".ai-item")];
    const focused = this._dropdown.querySelector(".ai-item:focus");
    const idx = focused ? items.indexOf(focused) : -1;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[Math.min(idx + 1, items.length - 1)]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (idx > 0) items[idx - 1].focus();
      else this._activeInput?.focus();
    } else if (e.key === "Escape") {
      this._hide();
      this._activeInput?.focus();
    } else if (e.key === "Enter") {
      // If a dropdown item is focused → follow its link
      if (focused) {
        e.preventDefault();
        window.location.href = focused.getAttribute("href");
        return;
      }
      // Otherwise navigate to the full AI search page
      const q = this._activeInput?.value.trim() ?? "";
      if (q.length >= MIN_CHARS) {
        e.preventDefault();
        window.location.href = `/pages/ai-search?q=${encodeURIComponent(q)}`;
      }
    }
  }

  async _search(query) {
    if (query === this._lastQuery) return;
    this._lastQuery = query;
    this._showLoading();

    const aiData = await this._fetchAI(query);

    if (query !== this._lastQuery) return;
    this._render(aiData, query);
  }

  async _fetchAI(query) {
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 7000);
      const resp = await fetch(AI_SEARCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + AI_ANON_KEY,
        },
        body: JSON.stringify({
          query,
          limit: AI_TOP_LIMIT,
          source: "dropdown",
          ...(AI_STORE_ID ? { store_id: AI_STORE_ID } : {}),
        }),
        signal: ctrl.signal,
      });
      clearTimeout(tid);
      return await resp.json();
    } catch {
      return { results: [], total: 0 };
    }
  }

  _getDropdown() {
    if (this._dropdown) return this._dropdown;
    const el = document.createElement("div");
    el.id = "hilason-ai-results";
    el.setAttribute("role", "listbox");
    this._dropdown = el;
    return el;
  }

  _showLoading() {
    const dd = this._getDropdown();
    dd.innerHTML = `
      <div class="ai-section-header">Product Results</div>
      <div class="ai-loading">Searching…</div>`;
    this._activeInput?.parentElement?.appendChild(dd);
  }

  _hide() {
    this._dropdown?.remove();
    this._dropdown = null;
    this._lastQuery = "";
  }

  _renderItem(r) {
    // onerror fallback: some item_cache.picture_url values are dead (404)
    // even though the field is non-null, so r.image truthiness alone isn't
    // a reliable signal the URL actually loads — swap to the same
    // placeholder used when there's no image at all.
    const noImgSvg = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" fill="none"></rect><circle cx="8.5" cy="8.5" r="1.5" fill="none"></circle><path d="M21 15l-5-5L5 21" fill="none"></path></svg>`;
    const thumb = r.image
      ? `<div class="ai-thumb-wrap"><img class="ai-thumb" src="${this._esc(r.image)}" alt="" loading="lazy" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'"><div class="ai-thumb-empty" style="display:none">${noImgSvg}</div></div>`
      : `<div class="ai-thumb-wrap ai-thumb-empty">${noImgSvg}</div>`;
    const exactBadge = r.score === 100 ? `<div class="ai-score-exact">Exact Match</div>` : "";
    const price = r.price != null
      ? `<div class="ai-price">$${parseFloat(r.price).toFixed(2)}</div>`
      : `<div class="ai-price-na"></div>`;
    return `
      <a class="ai-item" href="${this._esc(r.url)}" role="option" tabindex="0">
        ${thumb}
        ${exactBadge}
        <div class="ai-brand">${this._esc(r.sku||"")}</div>
        <div class="ai-title">${this._esc(r.title || r.sku)}</div>
        ${price}
      </a>`;
  }

  _render(aiData, query) {
    const dd = this._getDropdown();
    const aiResults = aiData.results ?? [];
    const seeAllUrl = `/pages/ai-search?q=${encodeURIComponent(query)}`;

    if (aiResults.length === 0) {
      dd.innerHTML = `
        <div class="ai-section-header">Product Results</div>
        <div class="ai-empty">No matches found for "<strong>${this._esc(query)}</strong>"
           — <a href="${seeAllUrl}">try full search</a></div>`;
      this._activeInput?.parentElement?.appendChild(dd);
      return;
    }

    const seeAllLink = `<a class="ai-see-all-top" href="${seeAllUrl}">See all →</a>`;
    let html = `<div class="ai-section-header">Top Matches${seeAllLink}</div>`;
    html += `<div class="ai-grid">${aiResults.map((r) => this._renderItem(r)).join("")}</div>`;
    html += `<div class="ai-footer"><a href="${seeAllUrl}">See all results for "${this._esc(query)}" →</a></div>`;

    dd.innerHTML = html;
    this._activeInput?.parentElement?.appendChild(dd);
  }

  _esc(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}

if (new URLSearchParams(location.search).get("ai") !== "off") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new HilasonAISearch());
  } else {
    new HilasonAISearch();
  }
}
